import React, { useState, useRef, useEffect } from 'react';
import typography from '../../utils/typography';
import colors from '../../utils/colors';
import {
  PlayIcon,
  EditUndoIcon,
  RotateReverseIcon,
  PlusIcon,
  MoreHorizontalIcon,
  ChevronDownIcon,
  UploadIcon,
  EditIcon,
  SlidersIcon,
  MusicNoteIcon,
  MoreVerticalIcon
} from '../../utils/icons';

// Create PauseIcon component
const PauseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 4C5.45 4 5 4.45 5 5V19C5 19.55 5.45 20 6 20C6.55 20 7 19.55 7 19V5C7 4.45 6.55 4 6 4Z" fill="currentColor"/>
    <path d="M18 4C17.45 4 17 4.45 17 5V19C17 19.55 17.45 20 18 20C18.55 20 19 19.55 19 19V5C19 4.45 18.55 4 18 4Z" fill="currentColor"/>
  </svg>
);

const HooksEditor = ({ onClose, isNavCollapsed = false }) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(10);
  const [isPlaying, setIsPlaying] = useState(false);
  const [projectName, setProjectName] = useState('Untitled');
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [uploadedAudio, setUploadedAudio] = useState(null);
  const [videoThumbnails, setVideoThumbnails] = useState([]);
  const [selectedClip, setSelectedClip] = useState(null); // 'video' or 'audio' or null
  const [videoTrimStart, setVideoTrimStart] = useState(0);
  const [videoTrimEnd, setVideoTrimEnd] = useState(null);
  
  const timelineRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingTrimHandle, setIsDraggingTrimHandle] = useState(null); // 'start' or 'end'
  const [justFinishedDragging, setJustFinishedDragging] = useState(false);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProjectMenu && !event.target.closest('.project-dropdown')) {
        setShowProjectMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProjectMenu]);

  // Handle playhead and trim handle dragging
  React.useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging && timelineRef.current) {
        const rect = timelineRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left - 12; // Account for 12px padding
        const usableWidth = rect.width - 24; // Account for 12px padding on each side
        const percentage = Math.max(0, Math.min(1, clickX / usableWidth));
        const newTime = percentage * duration;
        setCurrentTime(newTime);
      }
      
      // Handle trim handle dragging
      if (isDraggingTrimHandle && timelineRef.current && uploadedVideo) {
        const rect = timelineRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left - 12;
        const usableWidth = rect.width - 24;
        const percentage = Math.max(0, Math.min(1, clickX / usableWidth));
        const newTime = percentage * duration;
        
        if (isDraggingTrimHandle === 'start') {
          setVideoTrimStart(Math.max(0, Math.min(newTime, videoTrimEnd - 0.1)));
        } else if (isDraggingTrimHandle === 'end') {
          setVideoTrimEnd(Math.min(duration, Math.max(newTime, videoTrimStart + 0.1)));
        }
      }
    };

    const handleMouseUp = () => {
      if (isDraggingTrimHandle) {
        setJustFinishedDragging(true);
        // Clear the flag after a short delay
        setTimeout(() => setJustFinishedDragging(false), 50);
      }
      setIsDragging(false);
      setIsDraggingTrimHandle(null);
    };

    if (isDragging || isDraggingTrimHandle) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isDraggingTrimHandle, duration, videoTrimStart, videoTrimEnd]);

  const handleTimelineClick = (e) => {
    if (timelineRef.current) {
      const rect = timelineRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left - 12; // Account for 12px padding
      const usableWidth = rect.width - 24; // Account for 12px padding on each side
      const percentage = Math.max(0, Math.min(1, clickX / usableWidth));
      const newTime = percentage * duration;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('video/')) {
      const videoUrl = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.src = videoUrl;
      video.addEventListener('loadedmetadata', async () => {
        setDuration(video.duration);
        setVideoTrimEnd(video.duration); // Initialize trim end to full duration
        
        // Generate thumbnails
        try {
          const thumbnails = await generateVideoThumbnails(file);
          setVideoThumbnails(thumbnails);
        } catch (error) {
          console.error('Error generating thumbnails:', error);
        }
      });
      
      setUploadedVideo({
        file: file,
        url: videoUrl,
        name: file.name
      });
    }
  };

  const handleAudioUpload = async (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('audio/')) {
      const audioUrl = URL.createObjectURL(file);
      const audio = document.createElement('audio');
      audio.src = audioUrl;
      
      audio.addEventListener('loadedmetadata', async () => {
        // For timeline display, use video duration or 15s max
        const videoDuration = uploadedVideo ? (videoRef.current?.duration || duration) : 0;
        const timelineDuration = videoDuration > 0 ? videoDuration : 15;
        
        // Don't extend timeline duration for audio
        if (!uploadedVideo) {
          setDuration(15);
        }
        
        // Generate waveform
        try {
          const waveformData = await generateAudioWaveform(file);
          setUploadedAudio({
            file: file,
            url: audioUrl,
            name: file.name,
            duration: timelineDuration, // Duration for timeline display
            originalDuration: audio.duration, // Full audio duration for looping
            waveform: waveformData
          });
        } catch (error) {
          console.error('Error generating waveform:', error);
          setUploadedAudio({
            file: file,
            url: audioUrl,
            name: file.name,
            duration: timelineDuration,
            originalDuration: audio.duration
          });
        }
      });
    }
  };

  const generateAudioWaveform = async (audioFile) => {
    return new Promise((resolve, reject) => {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const audioBuffer = await audioContext.decodeAudioData(e.target.result);
          const channelData = audioBuffer.getChannelData(0);
          const samples = 200; // Number of waveform bars
          const blockSize = Math.floor(channelData.length / samples);
          const waveformData = [];
          
          for (let i = 0; i < samples; i++) {
            let sum = 0;
            for (let j = 0; j < blockSize; j++) {
              sum += Math.abs(channelData[i * blockSize + j] || 0);
            }
            const average = sum / blockSize;
            waveformData.push(Math.min(average * 100, 100)); // Scale to 0-100
          }
          
          resolve(waveformData);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read audio file'));
      reader.readAsArrayBuffer(audioFile);
    });
  };

  const generateVideoThumbnails = async (videoFile, count = 15) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const thumbnails = [];
      
      video.crossOrigin = 'anonymous';
      video.src = URL.createObjectURL(videoFile);
      
      video.addEventListener('loadedmetadata', () => {
        // Higher resolution for crisp thumbnails
        canvas.width = 300;
        canvas.height = 250;
        let currentIndex = 0;
        
        const captureFrame = () => {
          if (currentIndex >= count) {
            resolve(thumbnails);
            return;
          }
          
          const time = (currentIndex / (count - 1)) * video.duration;
          video.currentTime = time;
        };
        
        video.addEventListener('seeked', () => {
          // Clear canvas and set high quality rendering
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataURL = canvas.toDataURL('image/png', 1.0);
          thumbnails.push(dataURL);
          currentIndex++;
          
          if (currentIndex < count) {
            captureFrame();
          } else {
            resolve(thumbnails);
          }
        });
        
        captureFrame();
      });
    });
  };

  const triggerVideoUpload = () => {
    fileInputRef.current?.click();
  };

  const triggerAudioUpload = () => {
    audioInputRef.current?.click();
  };

  const togglePlayback = () => {
    if (isPlaying) {
      // Pause both video and audio
      if (videoRef.current && uploadedVideo) {
        videoRef.current.pause();
      }
      if (audioRef.current && uploadedAudio) {
        audioRef.current.pause();
      }
    } else {
      // Play both video and audio
      if (videoRef.current && uploadedVideo) {
        // Map timeline position to actual video position within trim bounds
        const trimmedDuration = (videoTrimEnd || duration) - videoTrimStart;
        const videoPosition = videoTrimStart + (currentTime / duration) * trimmedDuration;
        videoRef.current.currentTime = videoPosition;
        videoRef.current.play();
      }
      if (audioRef.current && uploadedAudio) {
        // Calculate position in original audio based on looping
        const audioPosition = uploadedAudio.originalDuration ? 
          currentTime % uploadedAudio.originalDuration : currentTime;
        audioRef.current.currentTime = audioPosition;
        audioRef.current.play();
      }
    }
    setIsPlaying(!isPlaying);
  };

  // Update current time when video plays and handle looping
  useEffect(() => {
    const video = videoRef.current;
    const audio = audioRef.current;
    
    const updateTime = () => {
      if (video && uploadedVideo) {
        // Map video time back to timeline position considering trim
        const videoTime = video.currentTime;
        const trimmedDuration = (videoTrimEnd || duration) - videoTrimStart;
        
        // Check if video is outside trim bounds
        if (videoTime < videoTrimStart || videoTime > (videoTrimEnd || duration)) {
          // Jump back to trim start
          video.currentTime = videoTrimStart;
          return;
        }
        
        // Map video time to timeline position (0 to duration)
        const timelinePosition = ((videoTime - videoTrimStart) / trimmedDuration) * duration;
        setCurrentTime(Math.min(timelinePosition, duration));
      } else if (audio && uploadedAudio) {
        const newTime = audio.currentTime;
        setCurrentTime(newTime);
      }
    };

    const handleVideoEnded = () => {
      // When video ends, loop everything back to start
      if (video && uploadedVideo) {
        video.currentTime = videoTrimStart; // Loop to trim start, not 0
        video.play();
      }
      if (audio && uploadedAudio) {
        audio.currentTime = 0;
        audio.play();
      }
      setCurrentTime(0);
    };

    const handleAudioEnded = () => {
      // When audio ends naturally, loop it back to start
      if (audio && uploadedAudio && isPlaying) {
        audio.currentTime = 0;
        audio.play();
      }
    };

    const handleLoadedData = () => {
      // Enable looping for video
      if (video) {
        video.loop = false; // We'll handle looping manually for sync
      }
      if (audio) {
        audio.loop = false; // We'll handle looping manually
      }
    };

    if (video && uploadedVideo) {
      video.addEventListener('timeupdate', updateTime);
      video.addEventListener('ended', handleVideoEnded);
      video.addEventListener('loadeddata', handleLoadedData);
    }

    if (audio && uploadedAudio) {
      audio.addEventListener('ended', handleAudioEnded);
      audio.addEventListener('loadeddata', handleLoadedData);
      
      // If no video, use audio for time updates
      if (!video || !uploadedVideo) {
        audio.addEventListener('timeupdate', updateTime);
      }
    }

    return () => {
      if (video) {
        video.removeEventListener('timeupdate', updateTime);
        video.removeEventListener('ended', handleVideoEnded);
        video.removeEventListener('loadeddata', handleLoadedData);
      }
      if (audio) {
        audio.removeEventListener('timeupdate', updateTime);
        audio.removeEventListener('ended', handleAudioEnded);
        audio.removeEventListener('loadeddata', handleLoadedData);
      }
    };
  }, [uploadedVideo, uploadedAudio, isPlaying]);

  // Update video and audio current time when scrubbing
  useEffect(() => {
    if (!isDragging && !isPlaying) {
      if (videoRef.current && uploadedVideo) {
        // Map timeline position to actual video position within trim bounds
        const trimmedDuration = (videoTrimEnd || duration) - videoTrimStart;
        const videoPosition = videoTrimStart + (currentTime / duration) * trimmedDuration;
        videoRef.current.currentTime = videoPosition;
      }
      if (audioRef.current && uploadedAudio) {
        // Calculate position in audio based on looping
        const audioPosition = uploadedAudio.originalDuration ? 
          currentTime % uploadedAudio.originalDuration : currentTime;
        audioRef.current.currentTime = audioPosition;
      }
    }
  }, [currentTime, isDragging, isPlaying, uploadedVideo, uploadedAudio, videoTrimStart, videoTrimEnd]);

  // Handle video state changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !uploadedVideo) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [uploadedVideo]);

  const generateTimeMarkers = () => {
    const markers = [];
    const step = 2; // Every 2 seconds
    const maxMarkers = Math.min(Math.ceil(duration / step), 6); // Limit to 6 markers max for layout
    
    for (let i = 0; i < maxMarkers; i++) {
      const time = i * step;
      markers.push(
        <div
          key={time}
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            flexGrow: 0,
            flexShrink: 0,
            width: 52,
            position: "relative",
            gap: 8,
          }}
        >
          <svg
            width={2}
            height={14}
            viewBox="0 0 2 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ flexGrow: 0, flexShrink: 0 }}
            preserveAspectRatio="none"
          >
            <path d="M1 0V14" stroke="white" strokeOpacity="0.3" />
          </svg>
          <p
            style={{
              flexGrow: 0,
              flexShrink: 0,
              fontSize: 10,
              fontWeight: 500,
              fontFamily: "Input Sans, monospace",
              textAlign: "right",
              color: "rgba(255,255,255,0.3)",
              margin: 0
            }}
          >
            {formatTime(time)}
          </p>
        </div>
      );
    }
    return markers;
  };

  const navbarWidth = isNavCollapsed ? 76 : 220;
  
  return (
    <div 
      className="fixed inset-0 z-30 overflow-hidden"
      style={{ 
        backgroundColor: colors.bg.primary,
        paddingLeft: `${navbarWidth}px` // Account for navbar width
      }}
    >
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        onChange={handleVideoUpload}
        style={{ display: 'none' }}
      />
      <input
        ref={audioInputRef}
        type="file"
        accept="audio/*"
        onChange={handleAudioUpload}
        style={{ display: 'none' }}
      />

      {/* Centered Editor Container */}
      <div className="w-full h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 relative" style={{ height: '80px', padding: '0 24px' }}>
        <div className="flex items-center gap-4">
          <h2 className="text-white" style={typography.large.title}>
            Edit Hook
          </h2>
        </div>
        
        {/* Centered Project Dropdown - centered in viewport */}
        <div className="absolute left-1/2 transform -translate-x-1/2">
          <div className="relative project-dropdown">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexGrow: 0,
                flexShrink: 0,
                height: 40,
                position: "relative",
                gap: 8,
                padding: 12,
                borderRadius: 100,
                background: "rgba(255,255,255,0.04)",
                backdropFilter: "blur(32px)",
                cursor: "pointer"
              }}
              onClick={() => setShowProjectMenu(!showProjectMenu)}
            >
              <img
                style={{ flexGrow: 0, flexShrink: 0, width: 16, height: 16, borderRadius: 34 }}
                src="rectangle-34629792.png"
              />
              <p
                style={{
                  flexGrow: 0,
                  flexShrink: 0,
                  fontSize: 14,
                  fontWeight: 500,
                  textAlign: "center",
                  color: "#faf7f5",
                  margin: 0
                }}
              >
                {projectName}
              </p>
              <svg
                width={17}
                height={16}
                viewBox="0 0 17 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ flexGrow: 0, flexShrink: 0, width: 16, height: 16, position: "relative" }}
                preserveAspectRatio="xMidYMid meet"
              >
                <path
                  d="M4.50008 9.33317C4.13341 9.33317 3.81953 9.20261 3.55841 8.9415C3.2973 8.68039 3.16675 8.3665 3.16675 7.99984C3.16675 7.63317 3.2973 7.31928 3.55841 7.05817C3.81953 6.79706 4.13341 6.6665 4.50008 6.6665C4.86675 6.6665 5.18064 6.79706 5.44175 7.05817C5.70286 7.31928 5.83341 7.63317 5.83341 7.99984C5.83341 8.3665 5.70286 8.68039 5.44175 8.9415C5.18064 9.20261 4.86675 9.33317 4.50008 9.33317ZM8.50008 9.33317C8.13341 9.33317 7.81953 9.20261 7.55841 8.9415C7.2973 8.68039 7.16675 8.3665 7.16675 7.99984C7.16675 7.63317 7.2973 7.31928 7.55841 7.05817C7.81953 6.79706 8.13341 6.6665 8.50008 6.6665C8.86675 6.6665 9.18064 6.79706 9.44175 7.05817C9.70286 7.31928 9.83341 7.63317 9.83341 7.99984C9.83341 8.3665 9.70286 8.68039 9.44175 8.9415C9.18064 9.20261 8.86675 9.33317 8.50008 9.33317ZM12.5001 9.33317C12.1334 9.33317 11.8195 9.20261 11.5584 8.9415C11.2973 8.68039 11.1667 8.3665 11.1667 7.99984C11.1667 7.63317 11.2973 7.31928 11.5584 7.05817C11.8195 6.79706 12.1334 6.6665 12.5001 6.6665C12.8667 6.6665 13.1806 6.79706 13.4417 7.05817C13.7029 7.31928 13.8334 7.63317 13.8334 7.99984C13.8334 8.3665 13.7029 8.68039 13.4417 8.9415C13.1806 9.20261 12.8667 9.33317 12.5001 9.33317Z"
                  fill="#F7F4EF"
                />
              </svg>
            </div>
            
            {/* Project Menu Dropdown */}
            {showProjectMenu && (
              <div 
                className="absolute top-full mt-2 right-0 min-w-48 rounded-lg border border-white/10 shadow-lg z-10"
                style={{ backgroundColor: colors.bg.tertiary }}
              >
                <div className="py-1">
                  <button 
                    className="w-full px-4 py-2 text-left text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                    style={typography.small.regular}
                    onClick={() => {
                      // Handle save as new project
                      setShowProjectMenu(false);
                    }}
                  >
                    Save as New Project
                  </button>
                  <button 
                    className="w-full px-4 py-2 text-left text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                    style={typography.small.regular}
                    onClick={() => {
                      // Handle new project
                      setProjectName('Untitled');
                      setShowProjectMenu(false);
                    }}
                  >
                    New Project
                  </button>
                  <button 
                    className="w-full px-4 py-2 text-left text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                    style={typography.small.regular}
                    onClick={() => {
                      // Handle open existing project
                      setShowProjectMenu(false);
                    }}
                  >
                    Open Existing Project
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-white/70 hover:text-white transition-colors rounded-full"
            style={{ ...typography.medium.title, backgroundColor: colors.bg.secondary }}
          >
            Cancel
          </button>
          <button
            className="px-6 py-2 bg-white text-black rounded-full hover:bg-white/90 transition-colors"
            style={typography.medium.title}
          >
            Next
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col h-full">
        {/* Video Preview Area */}
        <div className="flex items-center justify-center" style={{ paddingTop: "24px", paddingBottom: "24px" }}>
          <div style={{ width: 176, height: 306, position: "relative" }}>
            <div
              onClick={triggerVideoUpload}
              style={{
                width: 176,
                height: 306,
                borderRadius: 20,
                background: "#1c1c1f",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden"
              }}
            >
              {uploadedVideo ? (
                <video
                  ref={videoRef}
                  src={uploadedVideo.url}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: 20
                  }}
                  controls={false}
                  muted
                />
              ) : (
                <>
                  <svg
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      pointerEvents: "none"
                    }}
                    viewBox="0 0 176 306"
                  >
                    <rect
                      x="1"
                      y="1"
                      width="174"
                      height="304"
                      rx="19"
                      ry="19"
                      fill="none"
                      stroke="rgba(255,255,255,0.5)"
                      strokeWidth="0.5"
                      strokeDasharray="8 10"
                    />
                  </svg>
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <svg
                      width={54}
                      height={54}
                      viewBox="0 0 54 54"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ flexGrow: 0, flexShrink: 0, width: 54, height: 54 }}
                      preserveAspectRatio="none"
                    >
                      <circle cx={27} cy={27} r={27} fill="#252529" />
                      <path
                        d="M26.874 30.9277C26.3217 30.9277 25.874 30.48 25.874 29.9277V22.7777L23.9936 24.6581C23.5983 25.0535 22.9501 25.0423 22.5617 24.64C22.1829 24.2476 22.1831 23.6186 22.5688 23.2329L26.1669 19.6348C26.5574 19.2443 27.1906 19.2443 27.5811 19.6348L31.1792 23.2329C31.5649 23.6186 31.5652 24.2476 31.1863 24.64C30.7979 25.0423 30.1498 25.0535 29.7544 24.6581L27.874 22.7777V29.9277C27.874 30.48 27.4263 30.9277 26.874 30.9277ZM20.874 34.9277C20.324 34.9277 19.8532 34.7319 19.4615 34.3402C19.0699 33.9486 18.874 33.4777 18.874 32.9277V30.9277C18.874 30.3754 19.3217 29.9277 19.874 29.9277C20.4263 29.9277 20.874 30.3754 20.874 30.9277V32.9277H32.874V30.9277C32.874 30.3754 33.3217 29.9277 33.874 29.9277C34.4263 29.9277 34.874 30.3754 34.874 30.9277V32.9277C34.874 33.4777 34.6782 33.9486 34.2865 34.3402C33.8949 34.7319 33.424 34.9277 32.874 34.9277H20.874Z"
                        fill="#F7F4EF"
                      />
                    </svg>
                    <p
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        textAlign: "center",
                        color: "#bab4b1",
                        margin: 0,
                        whiteSpace: "nowrap"
                      }}
                    >
                      Upload a Video
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="px-8">
          <div className="flex items-center justify-center" style={{ marginBottom: "12px" }}>
            <div style={{ width: 624, height: 40, position: "relative", maxWidth: "624px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  height: 40,
                  position: "absolute",
                  left: 0,
                  top: 0,
                  overflow: "hidden",
                  gap: 8,
                  borderRadius: 38,
                  background: "rgba(255,255,255,0.04)",
                }}
              >
                <svg
                  width={40}
                  height={40}
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexGrow: 0, flexShrink: 0, width: 40, height: 40, opacity: "0.5", cursor: "pointer" }}
                  preserveAspectRatio="none"
                >
                  <g opacity="0.5">
                    <path
                      d="M16.4648 27.3824C15.9126 27.3824 15.4648 26.9347 15.4648 26.3824C15.4648 25.8301 15.9126 25.3824 16.4648 25.3824H22.5648C23.6148 25.3824 24.5273 25.0491 25.3023 24.3824C26.0773 23.7158 26.4648 22.8824 26.4648 21.8824C26.4648 20.8824 26.0773 20.0491 25.3023 19.3824C24.5273 18.7158 23.6148 18.3824 22.5648 18.3824H16.2648L18.1648 20.2824C18.5514 20.669 18.5514 21.2958 18.1648 21.6824C17.7782 22.069 17.1514 22.069 16.7648 21.6824L12.4648 17.3824L16.7648 13.0824C17.1514 12.6958 17.7782 12.6958 18.1648 13.0824C18.5514 13.469 18.5514 14.0958 18.1648 14.4824L16.2648 16.3824H22.5648C24.1815 16.3824 25.569 16.9074 26.7273 17.9574C27.8857 19.0074 28.4648 20.3158 28.4648 21.8824C28.4648 23.4491 27.8857 24.7574 26.7273 25.8074C25.569 26.8574 24.1815 27.3824 22.5648 27.3824H16.4648Z"
                      fill="#FAF7F5"
                    />
                  </g>
                </svg>
                <svg
                  width={40}
                  height={40}
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexGrow: 0, flexShrink: 0, width: 40, height: 40, opacity: "0.5", cursor: "pointer" }}
                  preserveAspectRatio="none"
                >
                  <g opacity="0.5">
                    <path
                      d="M17.9 27.5001C16.2833 27.5001 14.8958 26.9751 13.7375 25.9251C12.5792 24.8751 12 23.5668 12 22.0001C12 20.4334 12.5792 19.1251 13.7375 18.0751C14.8958 17.0251 16.2833 16.5001 17.9 16.5001H24.2L22.3 14.6001C21.9134 14.2135 21.9134 13.5867 22.3 13.2001C22.6866 12.8135 23.3134 12.8135 23.7 13.2001L27.2929 16.793C27.6834 17.1835 27.6834 17.8167 27.2929 18.2072L23.7 21.8001C23.3134 22.1867 22.6866 22.1867 22.3 21.8001C21.9134 21.4135 21.9134 20.7867 22.3 20.4001L24.2 18.5001H17.9C16.85 18.5001 15.9375 18.8334 15.1625 19.5001C14.3875 20.1668 14 21.0001 14 22.0001C14 23.0001 14.3875 23.8334 15.1625 24.5001C15.9375 25.1668 16.85 25.5001 17.9 25.5001H24C24.5523 25.5001 25 25.9478 25 26.5001C25 27.0524 24.5523 27.5001 24 27.5001H17.9Z"
                      fill="#FAF7F5"
                    />
                  </g>
                </svg>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "absolute",
                  left: "50%",
                  top: 5,
                  transform: "translateX(-50%)",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexGrow: 0,
                    flexShrink: 0,
                    width: 30,
                    height: 30,
                    borderRadius: 50,
                    background: "#f7f4ef",
                    cursor: "pointer"
                  }}
                  onClick={togglePlayback}
                >
                  {isPlaying ? (
                    <svg
                      width={18}
                      height={18}
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ flexGrow: 0, flexShrink: 0, width: 18, height: 18, position: "relative" }}
                      preserveAspectRatio="xMidYMid meet"
                    >
                      <path d="M5 3C4.45 3 4 3.45 4 4V14C4 14.55 4.45 15 5 15C5.55 15 6 14.55 6 14V4C6 3.45 5.55 3 5 3Z" fill="#101012"/>
                      <path d="M13 3C12.45 3 12 3.45 12 4V14C12 14.55 12.45 15 13 15C13.55 15 14 14.55 14 14V4C14 3.45 13.55 3 13 3Z" fill="#101012"/>
                    </svg>
                  ) : (
                    <svg
                      width={18}
                      height={18}
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ flexGrow: 0, flexShrink: 0, width: 18, height: 18, position: "relative" }}
                      preserveAspectRatio="xMidYMid meet"
                    >
                      <path
                        d="M4.75 14.0283L4.75 3.97166C4.75 3.69636 4.8537 3.46559 5.06111 3.27935C5.26852 3.09312 5.51049 3 5.78704 3C5.87346 3 5.9642 3.01215 6.05926 3.03644C6.15432 3.06073 6.24506 3.09717 6.33148 3.14575L14.7833 8.17409C14.9389 8.27126 15.0556 8.39271 15.1333 8.53846C15.2111 8.68421 15.25 8.83806 15.25 9C15.25 9.16194 15.2111 9.31579 15.1333 9.46154C15.0556 9.60729 14.9389 9.72875 14.7833 9.82591L6.33148 14.8543C6.24506 14.9028 6.15432 14.9393 6.05926 14.9636C5.9642 14.9879 5.87346 15 5.78704 15C5.51049 15 5.26852 14.9069 5.06111 14.7206C4.8537 14.5344 4.75 14.3036 4.75 14.0283Z"
                        fill="#101012"
                      />
                    </svg>
                  )}
                </div>
                <p
                  style={{
                    fontSize: 10,
                    fontWeight: 500,
                    fontFamily: "Input Sans, monospace",
                    textAlign: "center",
                    color: "rgba(255,255,255,0.3)",
                    margin: 0,
                    whiteSpace: "nowrap"
                  }}
                >
                  {formatTime(currentTime)} / {formatTime(duration)}
                </p>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  position: "absolute",
                  right: 8,
                  top: 0,
                  gap: 10,
                  height: 40,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    flexGrow: 0,
                    flexShrink: 0,
                    height: 40,
                    borderRadius: 28,
                    background: "rgba(255,255,255,0.04)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      alignSelf: "stretch",
                      flexGrow: 0,
                      flexShrink: 0,
                      width: 40,
                      position: "relative",
                      gap: 10,
                      paddingLeft: 12,
                      paddingRight: 12,
                      borderRadius: 48,
                      background: "rgba(255,255,255,0.04)",
                      cursor: "pointer"
                    }}
                  >
                    <svg
                      width={16}
                      height={16}
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ flexGrow: 0, flexShrink: 0, width: 16, height: 16, position: "relative" }}
                      preserveAspectRatio="xMidYMid meet"
                    >
                      <path
                        d="M7.99665 9.76709L12.2216 5.5421C12.3653 5.3984 12.5354 5.32894 12.7318 5.33373C12.9282 5.33852 13.0983 5.41277 13.242 5.55647C13.3857 5.70018 13.4575 5.87023 13.4575 6.06663C13.4575 6.26303 13.3857 6.43309 13.242 6.57679L8.81578 10.9886C8.70082 11.1036 8.57148 11.1898 8.42777 11.2473C8.28407 11.3048 8.14036 11.3335 7.99665 11.3335C7.85294 11.3335 7.70924 11.3048 7.56553 11.2473C7.42182 11.1898 7.29249 11.1036 7.17752 10.9886L2.75134 6.56242C2.60764 6.41872 2.53818 6.25106 2.54297 6.05945C2.54776 5.86784 2.62201 5.70018 2.76571 5.55647C2.90942 5.41277 3.07947 5.34091 3.27587 5.34091C3.47227 5.34091 3.64233 5.41277 3.78603 5.55647L7.99665 9.76709Z"
                        fill="#F7F4EF"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline Ruler */}
          <div className="flex items-center justify-center" style={{ marginBottom: "24px" }}>
            <div
              style={{
                maxWidth: "624px",
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignSelf: "stretch",
                flexGrow: 0,
                flexShrink: 0,
                height: 32,
                paddingLeft: 12,
                paddingRight: 12,
                borderTopWidth: "0.5px",
                borderRightWidth: 0,
                borderBottomWidth: 0,
                borderLeftWidth: 0,
                borderColor: "rgba(255,255,255,0.3)",
                borderStyle: "solid",
                cursor: "pointer"
              }}
              onClick={(e) => {
                if (!justFinishedDragging) {
                  handleTimelineClick(e);
                  setSelectedClip(null); // Deselect any selected clips
                }
              }}
            >
              {generateTimeMarkers()}
            </div>
          </div>

          {/* Timeline Area with Playhead and Add Content Buttons */}
          <div className="flex items-center justify-center" style={{ marginBottom: "24px" }}>
            <div 
              style={{ maxWidth: "624px", width: "100%", position: "relative" }}
            >
              {/* Clickable Timeline Background Area - only for empty spaces */}
              <div
                ref={timelineRef}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  right: 0,
                  height: "60px", // Only covers the space above the Add buttons
                  cursor: "pointer",
                  zIndex: 5
                }}
                onClick={(e) => {
                  if (!justFinishedDragging) {
                    handleTimelineClick(e);
                    setSelectedClip(null); // Deselect any selected clips
                  }
                }}
              />
              {/* Playhead */}
              <div
                style={{
                  position: "absolute",
                  left: `calc(12px + ${(currentTime / duration)} * (100% - 24px) - 8px)`,
                  top: "-56px",
                  bottom: "-100px",
                  zIndex: 20,
                  cursor: isDragging ? "grabbing" : "grab",
                  overflow: "hidden"
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
              >
                <svg
                  width={16}
                  height="100%"
                  viewBox="0 0 16 300"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ width: 16, height: "100%" }}
                  preserveAspectRatio="none"
                >
                  <line
                    x1={8}
                    y1={16}
                    x2={8}
                    y2={300}
                    stroke="white"
                    strokeWidth={2}
                    strokeLinecap="round"
                  />
                  <circle cx={8} cy={8} r={6} fill="white" stroke="#101012" strokeWidth={2} />
                </svg>
              </div>

              {/* Add Content Buttons */}
              <div 
                className="flex flex-col gap-3"
                onClick={() => {
                  if (!justFinishedDragging) {
                    setSelectedClip(null); // Deselect when clicking in button area
                  }
                }}
              >
                {/* Video Track */}
                {uploadedVideo ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      alignSelf: "stretch",
                      flexGrow: 0,
                      flexShrink: 0,
                      height: 50,
                      overflow: "visible", // Allow handles to be visible
                      borderRadius: 8,
                      position: "relative",
                      paddingLeft: 12,
                      paddingRight: 12,
                      zIndex: 10
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedClip(selectedClip === 'video' ? null : 'video');
                    }}
                  >
                    {/* Full filmstrip container - always full width */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        height: 50,
                        gap: 0,
                        padding: 0,
                        overflow: "hidden",
                        position: "absolute",
                        left: 12,
                        width: "calc(100% - 24px)", // Always full width
                        border: "2px solid transparent", // No border on container
                        boxShadow: "none"
                      }}
                    >
                      {/* Clipping mask for trimmed area */}
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: `${(videoTrimStart / duration) * 100}%`,
                          width: `${((videoTrimEnd || duration) - videoTrimStart) / duration * 100}%`,
                          height: "100%",
                          overflow: "hidden",
                          zIndex: 2,
                          border: selectedClip === 'video' ? '2px solid white' : '2px solid transparent',
                          boxShadow: selectedClip === 'video' ? '0 0 8px rgba(255, 255, 255, 0.3)' : 'none',
                          borderRadius: 8
                        }}
                      >
                        {/* Full filmstrip - always shows all thumbnails in original positions */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            height: 50,
                            width: `${duration / ((videoTrimEnd || duration) - videoTrimStart) * 100}%`,
                            marginLeft: `${-((videoTrimStart / duration) / (((videoTrimEnd || duration) - videoTrimStart) / duration)) * 100}%`,
                            gap: 0,
                            background: "#1c1c1f", // Background only behind actual filmstrip
                            borderRadius: 8
                          }}
                        >
                          {videoThumbnails.map((thumbnail, index) => {
                            const thumbnailWidth = `calc(100% / ${videoThumbnails.length})`;
                            
                            return (
                              <img
                                key={index}
                                src={thumbnail}
                                style={{
                                  width: thumbnailWidth,
                                  height: "50px",
                                  objectFit: "cover",
                                  borderRadius: 0,
                                  display: "block",
                                  flexShrink: 0,
                                  imageRendering: "crisp-edges",
                                  filter: "contrast(1.1) saturate(1.1)"
                                }}
                                alt={`Video frame ${index}`}
                              />
                            );
                          })}
                        </div>
                      </div>

                      {/* Dimmed areas outside trim range */}
                      {videoTrimStart > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: `${(videoTrimStart / duration) * 100}%`,
                            height: "100%",
                            background: "rgba(0, 0, 0, 0.7)",
                            zIndex: 3,
                            pointerEvents: "none"
                          }}
                        />
                      )}
                      {(videoTrimEnd || duration) < duration && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: `${((duration - (videoTrimEnd || duration)) / duration) * 100}%`,
                            height: "100%",
                            background: "rgba(0, 0, 0, 0.7)",
                            zIndex: 3,
                            pointerEvents: "none"
                          }}
                        />
                      )}
                    </div>

                    {/* Trim Handles - only show when video is selected */}
                    {selectedClip === 'video' && (
                      <>
                        {/* Left trim handle */}
                        <div
                          style={{
                            position: "absolute",
                            left: `calc(12px + ${(videoTrimStart / duration)} * (100% - 24px) - 6px)`,
                            top: 0,
                            width: 12,
                            height: 50,
                            background: "white",
                            borderRadius: "4px",
                            cursor: "ew-resize",
                            zIndex: 30,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            setIsDraggingTrimHandle('start');
                          }}
                        >
                          <div style={{
                            width: 2,
                            height: 20,
                            background: "#007AFF",
                            borderRadius: 1
                          }} />
                        </div>

                        {/* Right trim handle */}
                        <div
                          style={{
                            position: "absolute",
                            left: `calc(12px + ${((videoTrimEnd || duration) / duration)} * (100% - 24px) - 6px)`,
                            top: 0,
                            width: 12,
                            height: 50,
                            background: "white",
                            borderRadius: "4px",
                            cursor: "ew-resize",
                            zIndex: 30,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            setIsDraggingTrimHandle('end');
                          }}
                        >
                          <div style={{
                            width: 2,
                            height: 20,
                            background: "#007AFF",
                            borderRadius: 1
                          }} />
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div
                    onClick={(e) => {
                      e.stopPropagation(); // Prevents deselection and timeline seeking
                      triggerVideoUpload();
                    }}
                    style={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      alignSelf: "stretch",
                      flexGrow: 0,
                      flexShrink: 0,
                      height: 50,
                      overflow: "hidden",
                      borderRadius: 8,
                      cursor: "pointer",
                      zIndex: 10
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        alignSelf: "stretch",
                        flexGrow: 0,
                        flexShrink: 0,
                        width: 12,
                        position: "relative",
                        opacity: 0,
                        gap: 10,
                        paddingLeft: 6,
                        paddingRight: 6,
                        paddingTop: 16,
                        paddingBottom: 16,
                        borderTopLeftRadius: 10,
                        borderBottomLeftRadius: 10,
                        background: "#fff",
                      }}
                    >
                      <div
                        style={{
                          flexGrow: 0,
                          flexShrink: 0,
                          width: 2,
                          height: "8.8px",
                          borderRadius: 17,
                          background: "#0e0808",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        alignSelf: "stretch",
                        flexGrow: 1,
                        gap: 10,
                        padding: 4,
                        borderRadius: 8,
                        background: "#1c1c1f",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          flexGrow: 0,
                          flexShrink: 0,
                          position: "relative",
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexGrow: 0,
                            flexShrink: 0,
                            width: 24,
                            height: 24,
                            position: "relative",
                            gap: 10,
                            borderRadius: 50,
                            background: "#252529",
                          }}
                        >
                          <svg
                            width={16}
                            height={16}
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ flexGrow: 0, flexShrink: 0, width: 16, height: 16, position: "relative" }}
                            preserveAspectRatio="xMidYMid meet"
                          >
                            <path
                              d="M8 2C7.52661 2 7.14286 2.38376 7.14286 2.85714L7.14286 7.14286L2.85714 7.14286C2.38376 7.14286 2 7.52661 2 8C2 8.47339 2.38376 8.85714 2.85714 8.85714H7.14286L7.14286 13.1429C7.14286 13.6162 7.52661 14 8 14C8.47339 14 8.85714 13.6162 8.85714 13.1429L8.85714 8.85714L13.1429 8.85714C13.6162 8.85714 14 8.47339 14 8C14 7.52661 13.6162 7.14286 13.1429 7.14286L8.85714 7.14286V2.85714C8.85714 2.38376 8.47339 2 8 2Z"
                              fill="#F7F4EF"
                            />
                          </svg>
                        </div>
                        <p
                          style={{
                            fontSize: 10,
                            fontWeight: 500,
                            fontFamily: "Input Sans, monospace",
                            color: "rgba(255,255,255,0.3)",
                            margin: 0
                          }}
                        >
                          Add a Video
                        </p>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        alignSelf: "stretch",
                        flexGrow: 0,
                        flexShrink: 0,
                        width: 12,
                        position: "relative",
                        opacity: 0,
                        gap: 10,
                        paddingLeft: 6,
                        paddingRight: 6,
                        paddingTop: 16,
                        paddingBottom: 16,
                        borderTopLeftRadius: 10,
                        borderBottomLeftRadius: 10,
                        background: "#fff",
                      }}
                    >
                      <div
                        style={{
                          flexGrow: 0,
                          flexShrink: 0,
                          width: 2,
                          height: "8.8px",
                          borderRadius: 17,
                          background: "#0e0808",
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Audio Track */}
                {uploadedAudio ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      alignSelf: "stretch",
                      flexGrow: 0,
                      flexShrink: 0,
                      height: 50,
                      overflow: "hidden",
                      borderRadius: 8,
                      position: "relative",
                      paddingLeft: 12,
                      paddingRight: 12,
                      zIndex: 10
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Hidden audio element */}
                    <audio
                      ref={audioRef}
                      src={uploadedAudio.url}
                      style={{ display: 'none' }}
                    />
                    
                    {/* Song info overlay */}
                    <div
                      style={{
                        position: "absolute",
                        top: 4,
                        left: 16,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        zIndex: 10,
                        background: "rgba(0,0,0,0.3)",
                        padding: "4px 8px",
                        borderRadius: 12,
                        backdropFilter: "blur(4px)"
                      }}
                    >
                      <MusicNoteIcon style={{ width: 12, height: 12, color: "#fff" }} />
                      <span style={{ 
                        fontSize: 8, 
                        fontFamily: "Input Sans, monospace", 
                        color: "#fff",
                        fontWeight: 500,
                        whiteSpace: "nowrap"
                      }}>
                        {uploadedAudio.name.substring(0, 20)}...
                      </span>
                    </div>

                    {/* Time range overlay */}
                    <div
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 16,
                        background: "rgba(0,0,0,0.3)",
                        padding: "4px 8px",
                        borderRadius: 12,
                        backdropFilter: "blur(4px)",
                        zIndex: 10
                      }}
                    >
                      <span style={{ 
                        fontSize: 8, 
                        fontFamily: "Input Sans, monospace", 
                        color: "rgba(255,255,255,0.9)",
                        fontWeight: 500
                      }}>
                        {formatTime(0)}-{formatTime(uploadedVideo ? ((videoTrimEnd || duration) - videoTrimStart) : (uploadedAudio.duration || duration))}
                      </span>
                    </div>
                    
                    {/* Waveform container */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "end",
                        height: 50,
                        gap: 0,
                        padding: "5px 0",
                        borderRadius: 8,
                        background: "linear-gradient(135deg, #E91E63 0%, #AD1457 50%, #880E4F 100%)",
                        overflow: "hidden",
                        position: "absolute",
                        left: 12,
                        width: uploadedVideo 
                          ? `calc((100% - 24px) * ${((videoTrimEnd || duration) - videoTrimStart) / duration})` // Match trimmed video duration
                          : `calc((100% - 24px) * ${Math.min(uploadedAudio ? (uploadedAudio.duration || duration) / duration : 1, 1)})`, // Original behavior if no video
                        justifyContent: "space-between",
                        paddingLeft: 4,
                        paddingRight: 4
                      }}
                    >
                      {uploadedAudio.waveform && uploadedAudio.waveform.map((amplitude, index) => (
                        <div
                          key={index}
                          style={{
                            width: `calc(100% / ${uploadedAudio.waveform.length})`,
                            maxWidth: "3px",
                            minWidth: "1px",
                            height: `${Math.max(amplitude * 0.8, 2)}%`,
                            background: "rgba(255,255,255,0.9)",
                            borderRadius: "1px",
                            margin: "0 0.5px"
                          }}
                        />
                      ))}
                      
                      {/* Fallback if no waveform data */}
                      {!uploadedAudio.waveform && Array.from({ length: 50 }, (_, i) => (
                        <div
                          key={i}
                          style={{
                            width: "2px",
                            height: `${Math.random() * 80 + 10}%`,
                            background: "rgba(255,255,255,0.9)",
                            borderRadius: "1px",
                            margin: "0 0.5px"
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={(e) => {
                      e.stopPropagation(); // Prevents deselection and timeline seeking
                      triggerAudioUpload();
                    }}
                    style={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      alignSelf: "stretch",
                      flexGrow: 0,
                      flexShrink: 0,
                      height: 50,
                      overflow: "hidden",
                      borderRadius: 8,
                      cursor: "pointer",
                      zIndex: 10
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        alignSelf: "stretch",
                        flexGrow: 0,
                        flexShrink: 0,
                        width: 12,
                        position: "relative",
                        opacity: 0,
                        gap: 10,
                        paddingLeft: 6,
                        paddingRight: 6,
                        paddingTop: 16,
                        paddingBottom: 16,
                        borderTopLeftRadius: 10,
                        borderBottomLeftRadius: 10,
                        background: "#fff",
                      }}
                    >
                      <div
                        style={{
                          flexGrow: 0,
                          flexShrink: 0,
                          width: 2,
                          height: "8.8px",
                          borderRadius: 17,
                          background: "#0e0808",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        alignSelf: "stretch",
                        flexGrow: 1,
                        gap: 10,
                        padding: 4,
                        borderRadius: 8,
                        background: "#1c1c1f",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          flexGrow: 0,
                          flexShrink: 0,
                          position: "relative",
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexGrow: 0,
                            flexShrink: 0,
                            width: 24,
                            height: 24,
                            position: "relative",
                            gap: 10,
                            borderRadius: 50,
                            background: "#252529",
                          }}
                        >
                          <svg
                            width={16}
                            height={16}
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ flexGrow: 0, flexShrink: 0, width: 16, height: 16, position: "relative" }}
                            preserveAspectRatio="xMidYMid meet"
                          >
                            <path
                              d="M8 2C7.52661 2 7.14286 2.38376 7.14286 2.85714L7.14286 7.14286L2.85714 7.14286C2.38376 7.14286 2 7.52661 2 8C2 8.47339 2.38376 8.85714 2.85714 8.85714H7.14286L7.14286 13.1429C7.14286 13.6162 7.52661 14 8 14C8.47339 14 8.85714 13.6162 8.85714 13.1429L8.85714 8.85714L13.1429 8.85714C13.6162 8.85714 14 8.47339 14 8C14 7.52661 13.6162 7.14286 13.1429 7.14286L8.85714 7.14286V2.85714C8.85714 2.38376 8.47339 2 8 2Z"
                              fill="#F7F4EF"
                            />
                          </svg>
                        </div>
                        <p
                          style={{
                            fontSize: 10,
                            fontWeight: 500,
                            fontFamily: "Input Sans, monospace",
                            color: "rgba(255,255,255,0.3)",
                            margin: 0
                          }}
                        >
                          Add a Song
                        </p>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        alignSelf: "stretch",
                        flexGrow: 0,
                        flexShrink: 0,
                        width: 12,
                        position: "relative",
                        opacity: 0,
                        gap: 10,
                        paddingLeft: 6,
                        paddingRight: 6,
                        paddingTop: 16,
                        paddingBottom: 16,
                        borderTopLeftRadius: 10,
                        borderBottomLeftRadius: 10,
                        background: "#fff",
                      }}
                    >
                      <div
                        style={{
                          flexGrow: 0,
                          flexShrink: 0,
                          width: 2,
                          height: "8.8px",
                          borderRadius: 17,
                          background: "#0e0808",
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Toolbar */}
      <div 
        className="flex items-center justify-center border-t border-white/10" 
        style={{ 
          backgroundColor: colors.bg.primary, 
          position: 'fixed', 
          bottom: 0, 
          left: `${navbarWidth}px`, // Account for navbar width dynamically
          right: 0, 
          height: '100px',
          zIndex: 30 
        }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              opacity: "0.5",
              gap: 10,
              padding: 8,
              borderRadius: 90,
              background: "rgba(255,255,255,0.04)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                alignSelf: "stretch",
                flexGrow: 0,
                flexShrink: 0,
                gap: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexGrow: 0,
                  flexShrink: 0,
                  width: 120,
                  height: 40,
                  position: "relative",
                  gap: 8,
                  paddingLeft: 16,
                  paddingRight: 16,
                  borderRadius: 20,
                  background: "rgba(255,255,255,0.06)",
                  cursor: "pointer"
                }}
              >
                <EditIcon style={{ width: 16, height: 16, color: "#f7f4ef" }} />
                <p
                  style={{
                    flexGrow: 0,
                    flexShrink: 0,
                    fontSize: 12,
                    fontWeight: 500,
                    textAlign: "center",
                    color: "#f7f4ef",
                    margin: 0
                  }}
                >
                  Trim Video
                </p>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexGrow: 0,
                  flexShrink: 0,
                  width: 120,
                  height: 40,
                  position: "relative",
                  gap: 8,
                  paddingLeft: 16,
                  paddingRight: 16,
                  borderRadius: 20,
                  background: "rgba(255,255,255,0.06)",
                  cursor: "pointer"
                }}
              >
                <MusicNoteIcon style={{ width: 16, height: 16, color: "#f7f4ef" }} />
                <p
                  style={{
                    flexGrow: 0,
                    flexShrink: 0,
                    fontSize: 12,
                    fontWeight: 500,
                    textAlign: "center",
                    color: "#f7f4ef",
                    margin: 0
                  }}
                >
                  Adjust Audio
                </p>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexGrow: 0,
                  flexShrink: 0,
                  width: 120,
                  height: 40,
                  position: "relative",
                  gap: 8,
                  paddingLeft: 16,
                  paddingRight: 16,
                  borderRadius: 20,
                  background: "rgba(255,255,255,0.06)",
                  cursor: "pointer"
                }}
              >
                <SlidersIcon style={{ width: 16, height: 16, color: "#f7f4ef" }} />
                <p
                  style={{
                    flexGrow: 0,
                    flexShrink: 0,
                    fontSize: 12,
                    fontWeight: 500,
                    textAlign: "center",
                    color: "#f7f4ef",
                    margin: 0
                  }}
                >
                  Volume
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HooksEditor;